import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Fetches the current user's "signal" fields from Supabase in parallel:
 *   - `profiles` (main_academy_id, profession, city, state, country)
 *   - `profile_hobbies` (existence check + hobby ids for interest matching)
 *
 * Re-fetches whenever `userId` or `refreshKey` changes.  `refreshKey` is
 * incremented externally (e.g. after a wizard save) to force a re-fetch
 * without unmounting the consumer.
 */
export interface MySignals {
  main_academy_id: string | null;
  profession: string | null;
  has_interests: boolean;
  hobby_ids: number[];
  city: string | null;
  state: string | null;
  country: string | null;
}

export function useMySignals(userId: string | undefined, refreshKey: number = 0) {
  const [signals, setSignals] = useState<MySignals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    (async () => {
      const [profileRes, hobbiesRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('main_academy_id, profession, city, state, country')
          .eq('id', userId)
          .single(),
        supabase
          .from('profile_hobbies')
          .select('hobby_id')
          .eq('profile_id', userId),
      ]);

      if (!mounted) return;

      const p = profileRes.data;
      const hobbyIds = (hobbiesRes.data ?? []).map(row => row.hobby_id);
      setSignals({
        main_academy_id: p?.main_academy_id ?? null,
        profession: p?.profession ?? null,
        has_interests: hobbyIds.length > 0,
        hobby_ids: hobbyIds,
        city: p?.city ?? null,
        state: p?.state ?? null,
        country: p?.country ?? null,
      });
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [userId, refreshKey]);

  return { signals, loading };
}
